import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, AddNewButton } from "@wayflyer/flyui";
import {
  RhfAutocompleteField,
  RhfDropdownField,
  type RhfAutocompleteFieldProps,
  type RhfDropdownFieldProps,
} from "@wayflyer/flyui-hook-form";
import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { object, string } from "yup";

import { NOT_LISTED } from "../../utils/funding-journey";
import { COMMON_MESSAGES } from "../common-messages";

type AddNewPlatformFormFields = {
  unsupportedPlatformName: string;
};

export const AddCustomPlatformInput = ({
  options: optionsIn,
  addUnsupportedPlatform,
  creatable = true,
}: {
  options: string[];
  addUnsupportedPlatform?: (value: string) => void;
  creatable?: boolean;
}) => {
  const intl = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const options = useMemo(() => {
    return optionsIn.map((option) =>
      option === NOT_LISTED
        ? {
            id: option,
            text: intl.formatMessage(COMMON_MESSAGES.iDontSeeMyPlatformListed),
          }
        : {
            id: option,
            text: option,
          }
    );
  }, [optionsIn, intl]);

  const schema = object().shape({
    unsupportedPlatformName: string()
      .trim()
      .required(
        creatable
          ? intl.formatMessage({
              id: "funding-application: add another platform error",
              defaultMessage: "Please choose platform or add custom platform name",
              description: "Error message if customer does not choose or type a platform option",
            })
          : intl.formatMessage({
              id: "funding-application: add another platform error non creatable",
              defaultMessage: "Please select an option from the list",
              description: "Error message if customer does not choose an option from the provided list of platforms",
            })
      ),
  });

  const form = useForm<AddNewPlatformFormFields>({
    resolver: yupResolver(schema),
  });

  const closeAddingNewPlatform = () => {
    form.reset();
    setIsModalOpen(false);
  };

  const handleAddingPlatform = (value: string) => {
    addUnsupportedPlatform?.(value);
    closeAddingNewPlatform();
  };

  const handleSubmit = async () => {
    await form.trigger();
    form.formState.isValid && handleAddingPlatform(form.getValues("unsupportedPlatformName"));
  };

  const commonProps:
    | Omit<RhfAutocompleteFieldProps<AddNewPlatformFormFields, any>, "createAction" | "onChange">
    | Omit<RhfDropdownFieldProps<AddNewPlatformFormFields, any>, "createAction" | "onChange"> = {
    label: intl.formatMessage({
      id: "funding-application: platform name",
      defaultMessage: "Platform name",
    }),
    form: form,
    name: "unsupportedPlatformName",
    options: options,
    clearable: true,
  };

  return (
    <>
      <Modal
        title={intl.formatMessage({
          id: "funding-application: add another platform",
          defaultMessage: "Add another platform",
        })}
        isOpen={isModalOpen}
        onClose={closeAddingNewPlatform}
        primaryAction={{
          onAction: handleSubmit,
          content: intl.formatMessage({
            id: "funding-application: add another platform button confirm",
            defaultMessage: "Add platform",
            description: "Confirm button after customer has selected another platform to add",
          }),
          analyticsId: "add-unsupported-platform-button",
        }}
        tertiaryAction={{
          onAction: closeAddingNewPlatform,
          content: intl.formatMessage({
            id: "funding-application: add another platform button cancel",
            defaultMessage: "Cancel",
            description: "Cancel button if customer does not wish to add another platform",
          }),
          analyticsId: "cancel-add-unsupported-platform-button",
        }}
      >
        {creatable ? (
          <RhfAutocompleteField
            {...commonProps}
            createAction={{
              content: intl.formatMessage({
                id: "funding-application: add another platform option",
                defaultMessage: "Add platform",
                description: "Displays when platform cannot be found to allow it to be added to the list",
              }),
              onAction: (value: string) => handleAddingPlatform(value),
            }}
            clearable
          />
        ) : (
          // @ts-expect-error TS2322
          <RhfDropdownField {...commonProps} />
        )}
      </Modal>
      <AddNewButton
        className="add-platform-button"
        onPress={() => setIsModalOpen(true)}
        data-testid="add-platform-button"
        isDisabled={isModalOpen}
      >
        {intl.formatMessage({
          id: "funding-application: other",
          defaultMessage: "Other",
        })}
      </AddNewButton>
    </>
  );
};
